import React, {
  useState,
  useEffect,
  ReactNode,
  useCallback,
  ReactElement,
  useMemo,
} from 'react';

import {
  formatISO,
  format as formatDate,
  parse as parseDate,
  parseISO,
} from 'date-fns';
import { ru } from 'date-fns/locale';
import { ruRU } from '@mui/x-date-pickers/locales';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  DateCalendar as MUICalendar,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { Popover, FormControl, TextField } from '@mui/material';
import classNames from 'classnames';

import { CALENDAR_MIN_DATE, CALENDAR_MAX_DATE } from '../../services/calendar';
import { useCalendar } from '../../services/hooks';
import { Typography } from '../../Typography';
import { Box } from '../../Box';
import { Icon } from '../../Icon';
import { Label, Preloader } from '../common';
import { toDate } from '../../format';
import { InputMask } from '../../InputMask';

import { CalendarHeader } from './CalendarHeader';
import { Day, DayProps } from './Day';
import { useStyles } from './DateInput.styles';

const DATE_FORMAT = 'dd.MM.yyyy';
const EMPTY_INPUT_MASK = '__.__.____';

export interface DateInputProps {
  label?: string;
  name?: string;
  onChange: (date: string | undefined) => void;
  onClear?: () => void;
  onBlur?: () => void;
  onLogAction?: (type: string) => void;
  placeholder?: string;
  shouldDisableDate?: (date: Date | null) => boolean;
  required?: boolean;
  disablePast?: boolean;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  recognizedValue?: string;
  isRecognitionUsedBefore?: boolean;
  externalError?: string;
  otherSelectedDate?: string;
  value?: string;
  clearable?: boolean;
  alwaysShowClear?: boolean;
  error?: string;
  warning?: string;
  showRange?: boolean;
  loading?: boolean;
  tooltip?: ReactNode | null;
  id?: string;
  testId?: string;
  formBlur?: (name: string) => void;
  renderDayTooltip?: (day: Date | null) => ReactElement | null;
  renderDayFooter?: (
    day: Date | null,
    isInRange: boolean,
  ) => ReactElement | null;
}

/**
 * DateInput - компонент для выбора даты с выпадающим календарем.
 *
 * @param {DateInputProps} props - Свойства компонента.
 * @param {string} props.label - Метка для поля ввода.
 * @param {boolean} [props.required=false] - Является ли поле обязательным.
 * @param {string | null} [props.value=null] - Текущее выбранное значение даты.
 * @param {string} [props.name] - Имя поля для использования в формах.
 * @param {string} [props.id] - Уникальный идентификатор для поля ввода.
 * @param {string} [props.placeholder] - Текст плейсхолдера для поля ввода.
 * @param {boolean} [props.disabled=false] - Отключено ли поле ввода.
 * @param {boolean} [props.loading=false] - Загружаются ли данные.
 * @param {boolean} [props.loaded=false] - Загружены ли данные.
 * @param {(value: string | undefined) => void} [props.onChange] - Функция, вызываемая при изменении выбранной даты.
 * @param {(name: string) => void} [props.formBlur] - Функция, вызываемая при потере фокуса полем ввода.
 * @param {string} [props.error] - Текст ошибки, если поле ввода содержит ошибку.
 * @param {string} [props.externalError] - Внешний текст ошибки.
 * @param {string} [props.warning] - Текст предупреждения.
 * @param {string} [props.minDate] - Минимальная дата, доступная для выбора.
 * @param {string} [props.maxDate] - Максимальная дата, доступная для выбора.
 * @param {boolean} [props.disablePast=false] - Отключены ли прошедшие даты.
 * @param {(date: Date) => boolean} [props.shouldDisableDate] - Функция для отключения определённых дат.
 * @param {boolean} [props.clearable=false] - Может ли поле ввода быть очищено.
 * @param {boolean} [props.alwaysShowClear=false] - Всегда ли отображаться кнопка очистки.
 * @param {(date: Date) => void} [props.onLogAction] - Функция для логирования действия.
 * @param {string} [props.testId] - Опциональный ID для тестирования компонента.
 * @param {string} [props.tooltip] - Текст подсказки.
 * @param props.calendar - Объект календаря с методами для работы с датами.
 * @param props.format - Объект форматирования дат.
 * @param props.rest - Дополнительные свойства для передачи в компонент календаря.
 * @param props.classes - Объект стилей для компонента.
 * @param {boolean} [props.showRange=false] - Отображается ли диапазон дат.
 * @param {string} [props.otherSelectedDate] - Другая выбранная дата в диапазоне.
 * @param {() => void} [props.onBlur] - Функция, вызываемая при потере фокуса.
 * @param {(day: Date | null) => ReactElement | null} [props.renderDayTooltip] - функция, которая рендерит тултип для дня в каледаре.
 * @param {(day: Date | null, isInRange: boolean)  => ReactElement | null} [props.renderDayFooter] - функция, которая рендерит что-либо под цифрой даты дня в календаре.
 * @returns {JSX.Element} Возвращает JSX-элемент представляющий компонент DateInput.
 */

export const DateInput = ({
  label,
  name,
  onChange,
  formBlur,
  onClear,
  onLogAction,
  placeholder = 'ДД.ММ.ГГГГ',
  required = false,
  disablePast = false,
  disabled = false,
  minDate = CALENDAR_MIN_DATE,
  maxDate = CALENDAR_MAX_DATE,
  recognizedValue,
  isRecognitionUsedBefore,
  shouldDisableDate,
  externalError,
  otherSelectedDate,
  clearable = false,
  alwaysShowClear = false,
  error,
  warning,
  value,
  onBlur,
  loading = false,
  showRange = false,
  tooltip = null,
  testId = 'test-id-date-input-text-field',
  id,
  renderDayTooltip,
  renderDayFooter,
  ...rest
}: DateInputProps) => {
  const { classes } = useStyles();
  const calendar = useCalendar();

  const valueAsDate = useMemo(() => (value ? parseISO(value) : null), [value]);

  const [inputValue, setInputValue] = useState<string>(
    value ? formatDate(new Date(value), DATE_FORMAT, { locale: ru }) : '',
  );

  const [errorByShouldDisableDate, setErrorByShouldDisableDate] = useState<
    boolean | 'force'
  >(false);
  const [errorText, setErrorText] = useState(error);
  const [loaded, setLoaded] = useState(false);
  const [showClear, setShowClear] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  const open = Boolean(anchorEl);

  const getMaxDateText = useCallback(() => {
    if (!maxDate) return '';

    if (maxDate instanceof Date) return new Date(maxDate.getTime() + 1000);

    return maxDate;
  }, [maxDate]);

  /**
   * Функция, возвращающая текст ошибки при валидации даты.
   *
   * @param {Date | null} [date] - Новая дата
   * @returns {string | undefined}
   */
  const getErrorText = useCallback(
    (date: Date | null) => {
      if (!date) return undefined; // Обязательность поля не мы решаем

      if (!calendar.isValidDate(date)) {
        return 'Некорректная дата';
      }

      if (minDate > date) {
        return `Дата должна быть больше чем ${toDate(minDate.toString())}`;
      }

      if (maxDate < date) {
        return `Дата должна быть меньше чем ${toDate(
          getMaxDateText().toString(),
        )}`;
      }

      setErrorByShouldDisableDate(!!shouldDisableDate?.(date));

      return undefined;
    },
    [calendar, shouldDisableDate, getMaxDateText, maxDate, minDate],
  );

  /**
   * Функция обработки изменения даты в компоненте DateInput.
   *
   * @param {Date | null} [date] - Новая выбранная дата или undefined, если дата не выбрана.
   * @returns {void}
   */
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setLoaded(false);

      const newValue = event.target.value;
      setInputValue(newValue);

      if (!newValue || newValue === EMPTY_INPUT_MASK) {
        onChange(undefined);
        formBlur?.(name as string);
        setLoaded(true);
        return;
      }

      const newValueAsDate = parseDate(newValue, DATE_FORMAT, new Date());

      const errorMessage = getErrorText(newValueAsDate);
      if (errorMessage) {
        if (value) onChange(undefined);

        setErrorText(errorMessage);
      } else {
        const newValueIso = formatISO(newValueAsDate, {
          representation: 'date',
        });
        onLogAction?.('Введена дата');
        onChange(newValueIso);
        setErrorText(error);
      }

      formBlur?.(name as string);
      setLoaded(true);
    },
    [value, getErrorText, onChange, onLogAction, name, formBlur, error],
  );

  /**
   * Обработчик потери фокуса текстовым полем.
   *
   * @returns {void}
   */
  const handleInputBlur = useCallback(() => {
    if (calendar.isValidDate(valueAsDate) && !errorText) {
      const newValueIso = valueAsDate
        ? formatISO(new Date(valueAsDate), {
            representation: 'date',
          })
        : undefined;
      onChange(newValueIso);

      formBlur?.(name as string);
      return;
    }

    if (value && !error) {
      const oldValueAsDate = new Date(value);
      setInputValue(formatDate(oldValueAsDate, 'dd.MM.yyyy', { locale: ru }));
    } else {
      setInputValue('');
      if (value) onChange(undefined);
    }

    formBlur?.(name as string);
    setErrorText(inputValue !== EMPTY_INPUT_MASK ? errorText ?? error : error);
  }, [
    calendar,
    name,
    formBlur,
    valueAsDate,
    errorText,
    value,
    error,
    inputValue,
    onChange,
  ]);

  useEffect(() => {
    if (value) {
      const asDate = new Date(value);
      setInputValue(formatDate(asDate, DATE_FORMAT, { locale: ru }));
    }
  }, [value]);

  useEffect(() => {
    if (error && errorByShouldDisableDate !== 'force') {
      setErrorText(error);

      return;
    } else if (errorByShouldDisableDate) {
      setErrorByShouldDisableDate('force');
      setErrorText('Некорректная дата');
      onChange(undefined);
    } else {
      setErrorText(undefined);
    }
  }, [error, errorByShouldDisableDate, onChange]);

  useEffect(() => {
    if (recognizedValue && !value) {
      onChange(recognizedValue);

      formBlur?.(name as string);
    }
  }, [onChange, name, formBlur, recognizedValue, value]);

  useEffect(() => {
    if (onBlur && isRecognitionUsedBefore && !disabled && !recognizedValue) {
      onBlur();
    }
  }, [isRecognitionUsedBefore, disabled, onBlur]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    calendar.download(new Date()).then(() => setLoaded(true));
  }, [calendar]);

  /**
   * Функция для очистки значения даты.
   *
   * @param {React.MouseEvent<HTMLDivElement>} e - Событие клика мыши.
   * @returns {void}
   */
  const clearDateInput = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      setInputValue('');
      setLoaded(false);
      setShowClear(false);
      setAnchorEl(null);
      setErrorText(error);
      setErrorByShouldDisableDate(false);
      onChange(undefined);
      formBlur?.(name as string);
      onBlur?.();
      onClear?.();
      setTimeout(() => setLoaded(true), 500);
    },
    [error, onChange, formBlur, name, onBlur, onClear],
  );

  /**
   * Обработчик клика по иконке календаря.
   *
   * @param {React.MouseEvent<HTMLDivElement>} e - Событие клика мыши.
   * @returns {void}
   */
  const dateIconClickHandler = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (loaded && !disabled) {
        onLogAction?.('Открыть календарь');
        setAnchorEl(e.currentTarget);
      }
    },
    [disabled, loaded, onLogAction],
  );

  /**
   * Флаг, указывающий на то, что данные загружаются.
   *
   * @type {boolean}
   */
  const isLoading = !loaded || loading;

  /**
   * Функция для определения цвета иконки календаря.
   *
   * @param {boolean} [isDisabled] - Флаг, указывающий, что компонент отключен.
   * @param {boolean} [isError] - Флаг, указывающий, что есть ошибка.
   * @returns {string} - Цвет иконки: 'disabled', 'error' или 'primary'.
   */
  const getCalendarIconColor = useCallback(
    (isDisabled?: boolean, isError?: boolean) => {
      if (isDisabled) {
        return 'stroke.secondary';
      }
      if (isError) {
        return 'accent.text.error';
      }

      return 'original.brand.primary';
    },
    [],
  );

  /**
   * Функция для рендеринга окончания текстового поля (адорнмента).
   *
   * @param {TextFieldProps} props - Свойства текстового поля.
   * @returns {JSX.Element} - JSX элемент для окончания текстового поля.
   */
  const renderEndAdornment = useCallback(
    (props: { value: string; disabled?: boolean; error?: boolean }) => {
      if (isLoading) {
        return <Preloader />;
      }

      const propsValueAsDate = parseDate(props.value, DATE_FORMAT, new Date());

      return (
        <Box display="flex">
          {clearable &&
            calendar.isValidDate(propsValueAsDate) &&
            !props.disabled && (
              <Box
                display="flex"
                alignItems="center"
                className={classes.clearIcon}
                mr="12"
                height={24}
                onClick={clearDateInput}
              >
                <Icon
                  name="cancel_circle"
                  size="20"
                  color={props.error ? 'accent.text.error' : 'stroke.secondary'}
                />
              </Box>
            )}
          <Box
            onClick={(e: React.MouseEvent<HTMLDivElement>) => {
              e.stopPropagation();
              if (loaded && !props.disabled) {
                setAnchorEl(e.currentTarget);
              }
            }}
          >
            <Box
              onClick={dateIconClickHandler}
              display="flex"
              alignItems="center"
            >
              <Icon
                name="vkhrtek_calendar"
                size="24"
                color={getCalendarIconColor(props.disabled, !!props.error)}
              />
            </Box>
          </Box>
        </Box>
      );
    },
    [
      calendar,
      classes.clearIcon,
      clearDateInput,
      clearable,
      dateIconClickHandler,
      getCalendarIconColor,
      isLoading,
      loaded,
    ],
  );

  return (
    <FormControl className={classNames(classes.input, 'aqa_date_input')}>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={ru}
        localeText={
          ruRU.components.MuiLocalizationProvider.defaultProps.localeText
        }
      >
        {label && <Label label={label} required={required} tooltip={tooltip} />}
        <Box>
          <InputMask
            mask={isLoading ? '' : '99.99.9999'}
            value={isLoading ? 'Загрузка...' : inputValue}
            data-testid={testId}
            alwaysShowMask={false}
            disabled={isLoading || disabled}
            onBlur={handleInputBlur}
            onChange={handleInputChange}
          >
            <TextField
              classes={{ root: classes.textDateInput }}
              variant="outlined"
              name={name}
              fullWidth
              placeholder={placeholder || label}
              disabled={isLoading || disabled}
              {...(errorText && {
                error: !!externalError || !!errorText,
                helperText: externalError || errorText,
              })}
              {...(isLoading && {
                value: 'Загрузка...',
              })}
              slotProps={{
                input: {
                  id,
                  placeholder: placeholder,
                  className: 'textFieldInput aqa_dt_input',
                  classes: { adornedEnd: classes.adornedEnd },
                  endAdornment: renderEndAdornment({
                    value: inputValue,
                    disabled,
                    error: !!errorText,
                  }),
                },
              }}
            />
          </InputMask>
          {!externalError && !errorText && warning && (
            <Typography variant="caption" color="text.light.secondary">
              {warning}
            </Typography>
          )}
        </Box>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={() => {
            setAnchorEl(null);
            onBlur?.();
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Box className="aqa_calendar" pb="16">
            <MUICalendar
              {...rest}
              view="day"
              dayOfWeekFormatter={(day) =>
                formatDate(new Date(day), 'EEEEEE', { locale: ru })
              }
              value={valueAsDate}
              onMonthChange={(date) => calendar.download(date || undefined)}
              disablePast={disablePast}
              minDate={minDate}
              maxDate={maxDate}
              onChange={(date, isFinal) => {
                if (isFinal) {
                  onLogAction?.('Выбрана дата');
                  onChange(
                    date
                      ? formatISO(date, { representation: 'date' })
                      : undefined,
                  );
                  setErrorByShouldDisableDate(false);
                  setAnchorEl(null);
                  onBlur?.();
                  setTimeout(() => setShowClear(true), 200);
                }
              }}
              {...(shouldDisableDate ? { shouldDisableDate } : {})}
              slots={{
                calendarHeader: CalendarHeader,
                day: Day,
              }}
              slotProps={{
                day: {
                  onMouseEnter: (_, day) => {
                    setHoverDate(day);
                  },
                  onMouseLeave: (_, day) => {
                    if (
                      day &&
                      hoverDate &&
                      calendar.isSameDay(day, hoverDate)
                    ) {
                      setHoverDate(null);
                    }
                  },
                  testHoliday: (day) =>
                    !calendar.isSameDay(day, new Date()) &&
                    calendar.isHolidayOrWeekend(day),
                  testIsInRange: (day) => {
                    if (hoverDate || valueAsDate) {
                      const date = (hoverDate || valueAsDate) as Date | string;

                      return !!(
                        showRange &&
                        day &&
                        otherSelectedDate &&
                        calendar.startOfDay(new Date(otherSelectedDate)) <=
                          day &&
                        calendar.startOfDay(new Date(date)) >= day
                      );
                    }

                    return false;
                  },
                  testIsStart: (day) =>
                    !!(
                      otherSelectedDate &&
                      day &&
                      calendar.isSameDay(day, new Date(otherSelectedDate))
                    ),
                  testIsEnd: (day) => {
                    if (
                      (valueAsDate &&
                        calendar.isSameDay(day, new Date(valueAsDate))) ||
                      (otherSelectedDate &&
                        calendar.isSameDay(day, new Date(otherSelectedDate)))
                    ) {
                      return (
                        valueAsDate &&
                        !hoverDate &&
                        calendar.isSameDay(day, new Date(valueAsDate))
                      );
                    }

                    return hoverDate && calendar.isSameDay(day, hoverDate);
                  },
                  renderDayTooltip: renderDayTooltip,
                  renderDayFooter: renderDayFooter,
                } as Partial<DayProps>,
              }}
            />
            {clearable && value && (alwaysShowClear || showClear) && (
              <Box pt="16" px="16" onClick={clearDateInput}>
                <a href="#" className={classes.action}>
                  Сбросить
                </a>
              </Box>
            )}
          </Box>
        </Popover>
      </LocalizationProvider>
    </FormControl>
  );
};
